import React, { useReducer, useMemo } from 'react';
import { DiscoveryEvent, DiscoveryEventsFromView } from '../types';

const transformInitialState = (
    eventData: DiscoveryEventsFromView,
): EventState => {
    const futureIds = eventData.future_events.map((event) => event.id);
    const pastIds = eventData.past_events.map((event) => event.id);
    const normalizedEvents = [
        ...eventData.future_events,
        ...eventData.past_events,
    ].reduce((memo, event) => {
        return {
            ...memo,
            [event.id]: { ...event, collections: [] },
        };
    }, {});

    return {
        events: normalizedEvents,
        past: {
            events: pastIds,
            num: eventData.num_past_events,
            hasMore: eventData.has_next_past_page,
            currentPage: 1,
        },
        future: {
            events: futureIds,
            num: eventData.num_future_events,
            hasMore: eventData.has_next_future_page,
            currentPage: 1,
        },
    };
};

interface EventState {
    events: { [key: string]: DiscoveryEvent };
    past: {
        events: string[];
        num: number;
        hasMore: boolean;
        currentPage: number;
    };
    future: {
        events: string[];
        num: number;
        hasMore: boolean;
        currentPage: number;
    };
}

interface EventContextShape {
    state: EventState;
    dispatch: Function;
}

export const EventContext = React.createContext<Partial<EventContextShape>>({});

const updateEvent = ({
    state,
    id,
    data,
}: {
    state: EventState;
    id: string;
    data: any;
}) => ({
    ...state,
    events: {
        ...state.events,
        [id]: {
            ...state.events[id],
            ...data,
        },
    },
});

const eventReducer = (
    state: EventState,
    action: { type: string; payload: any },
) => {
    switch (action.type) {
        case 'update_event_collections': {
            return updateEvent({
                state,
                id: action.payload.id,
                data: {
                    collections: action.payload.collections,
                    collectionsFetched: true,
                },
            });
        }
        case 'save': {
            return updateEvent({
                state,
                id: action.payload.id,
                data: { bookmarked: action.payload.value },
            });
        }
        case 'add_future': {
            return {
                ...state,
                events: {
                    ...state.events,
                    ...action.payload.events,
                },
                future: {
                    ...state.future,
                    hasMore: action.payload.hasMore,
                    currentPage: action.payload.requestedPage,
                    events: [...state.future.events, ...action.payload.ids],
                },
            };
        }
        case 'add_past': {
            return {
                ...state,
                events: {
                    ...state.events,
                    ...action.payload.events,
                },
                past: {
                    ...state.past,
                    hasMore: action.payload.hasMore,
                    currentPage: action.payload.requestedPage,
                    events: [...state.past.events, ...action.payload.ids],
                },
            };
        }
        default:
            throw new Error();
    }
};

export const EventProvider: React.FunctionComponent<DiscoveryEventsFromView> = (
    props,
) => {
    const initialState = transformInitialState(props);
    const [state, dispatch] = useReducer(eventReducer, initialState);

    const contextValue = useMemo(() => {
        return { state, dispatch };
    }, [state, dispatch]);

    return (
        <EventContext.Provider value={contextValue}>
            {props.children}
        </EventContext.Provider>
    );
};
